import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  type AuthFlowType,
  type AuthenticationResultType,
} from '@aws-sdk/client-cognito-identity-provider';
import { create } from 'zustand';

const REGION = import.meta.env.VITE_AWS_REGION ?? 'us-east-1';
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_APP_CLIENT_ID;
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;

const client = new CognitoIdentityProviderClient({ region: REGION });

const SESSION_KEY = 'session';
const REDIRECT_PATH = '/login/callback';

// Held in sessionStorage rather than localStorage: the authorize redirect
// returns to the same tab, and these die with it instead of outliving the flow.
const PKCE_KEY = 'pkce_verifier';
const STATE_KEY = 'oauth_state';

export interface Session {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
  email: string;
  // True for tokens obtained from the hosted UI's authorization code exchange.
  // That flow is the only one that leaves a Cognito session cookie in the
  // browser, and the cookie is what sign-out has to visit /logout to clear.
  viaHostedUi?: boolean;
}

interface AuthState {
  session: Session | null;
  loading: boolean;
  error: string | null;
  needsOtpCode: boolean;
}

export const useAuthStore = create<AuthState>(() => ({
  session: loadSession(),
  loading: false,
  error: null,
  needsOtpCode: false,
}));

let challengeSession: string | null = null;
let pendingUsername: string | null = null;

// Raised when the flow cannot continue from the state it is in: a response
// shape that fits none of the expected branches, or a challenge answered with
// no session to answer against. The message describes the fault for the
// console; the user sees the mapped text for `AuthFlowError` instead.
class AuthFlowError extends Error {
  override name = 'AuthFlowError';
}

// Cognito's own prose reaches the user only when nothing here matches. AWS SDK
// v3 carries the exception type in `err.name`; `err.message` is a sentence like
// "Invalid code or auth state for the user." that contains no type name, so
// these tables are keyed on `name`.
//
// The email step collapses UserNotFound and NotAuthorized into one message so
// the UI never reveals whether an account exists, even if the user pool's
// prevent_user_existence_errors mask fails to apply.
const ACCOUNT_EXISTENCE_SAFE =
  "If an account exists for that email, we've sent you a code.";

// Cognito's managed email OTP for passwordless sign-in is 8 digits. The user
// pool exposes no setting for it, so this is a property of the service the
// sign-in form has to match: a field that accepts fewer digits truncates the
// emailed code and every verification comes back a mismatch.
export const OTP_CODE_LENGTH = 8;

const RESTART_OTP = 'That sign-in attempt expired. Request a new code.';

const SIGN_IN_ERRORS: Record<string, string> = {
  UserNotFoundException: ACCOUNT_EXISTENCE_SAFE,
  NotAuthorizedException: ACCOUNT_EXISTENCE_SAFE,
  InvalidParameterException: 'Enter a valid email address.',
  TooManyRequestsException: 'Too many attempts. Wait a moment and try again.',
  LimitExceededException: 'Too many attempts. Wait a moment and try again.',
  AuthFlowError: "We couldn't start sign in. Please try again.",
};

// NotAuthorizedException means a dead challenge session here, not a bad
// account, so it gets the restart message rather than the email-step
// existence-safe one. AuthFlowError lands here too: both leave the code form
// unable to succeed, and "Resend code" is the way out of either.
const OTP_ERRORS: Record<string, string> = {
  CodeMismatchException: "That code didn't match. Try again.",
  ExpiredCodeException: 'That code expired. Request a new one.',
  NotAuthorizedException: RESTART_OTP,
  AuthFlowError: RESTART_OTP,
  TooManyRequestsException: 'Too many attempts. Wait a moment and try again.',
};

function friendlyMessage(
  err: unknown,
  table: Record<string, string>,
  fallback: string,
): string {
  if (!(err instanceof Error)) return fallback;
  if (err instanceof AuthFlowError) {
    // The mapped text drops the detail, so keep it somewhere reachable.
    console.error('Auth flow cannot continue:', err.message);
  }
  return table[err.name] ?? err.message ?? fallback;
}

function loadSession(): Session | null {
  try {
    const stored = localStorage.getItem(SESSION_KEY);
    return stored ? (JSON.parse(stored) as Session) : null;
  } catch {
    return null;
  }
}

function saveSession(s: Session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

function clearStoredSession() {
  localStorage.removeItem(SESSION_KEY);
}

// Treated as expired this far before `exp` actually passes. A token accepted at
// the instant it dies still has to travel to the API, and Cognito and the
// backend both reject at `exp` with no margin of their own. A browser clock
// running behind real time widens the same gap: the token looks more alive here
// than it does to the verifier. Tokens live 60 minutes and refresh tokens 30
// days, so giving up the last minute costs one extra refresh per session-hour.
const EXPIRY_LEEWAY_SECONDS = 60;

function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { exp: number };
    return payload.exp - EXPIRY_LEEWAY_SECONDS < Math.floor(Date.now() / 1000);
  } catch {
    return true;
  }
}

function extractEmail(idToken: string): string {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1])) as {
      email?: string;
    };
    return payload.email ?? '';
  } catch {
    return '';
  }
}

function getRedirectUri(): string {
  return `${window.location.origin}${REDIRECT_PATH}`;
}

// The user pool's own domain, which serves the hosted UI and the OAuth
// endpoints. Distinct from the SDK endpoint `client` talks to.
function hostedUiUrl(path: string): string {
  return `https://${COGNITO_DOMAIN}.auth.${REGION}.amazoncognito.com${path}`;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// 32 bytes encodes to 43 characters, the shortest verifier RFC 7636 permits.
function randomUrlSafe(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

async function toChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier),
  );
  return base64Url(new Uint8Array(digest));
}

// The app client is public, so a bare authorization code is redeemable by
// anyone who obtains it from the callback URL. The PKCE challenge binds the
// code to this browser's verifier, and `state` binds the callback to the
// browser that started the flow. Async because SHA-256 digests are.
export async function getGoogleSignInUrl(): Promise<string> {
  const verifier = randomUrlSafe(32);
  const state = randomUrlSafe(16);
  sessionStorage.setItem(PKCE_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: USER_POOL_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    scope: 'openid email',
    identity_provider: 'Google',
    state,
    code_challenge: await toChallenge(verifier),
    // Cognito rejects `plain`; S256 is the only method it accepts.
    code_challenge_method: 'S256',
  });
  return hostedUiUrl(`/oauth2/authorize?${params.toString()}`);
}

async function applyAuthResult(
  authResult: AuthenticationResultType,
  email: string,
  viaHostedUi = false,
) {
  if (!authResult.AccessToken || !authResult.IdToken) {
    throw new AuthFlowError(
      `Cognito returned an incomplete token set (accessToken: ${!!authResult.AccessToken}, idToken: ${!!authResult.IdToken})`,
    );
  }
  const newSession: Session = {
    accessToken: authResult.AccessToken,
    idToken: authResult.IdToken,
    refreshToken: authResult.RefreshToken,
    email: email || extractEmail(authResult.IdToken),
    viaHostedUi,
  };
  // Reached only by sign-in, never by refresh, so this cannot cancel the
  // refresh that produced its own argument.
  invalidateRefresh();
  saveSession(newSession);
  useAuthStore.setState({
    session: newSession,
    needsOtpCode: false,
    error: null,
  });
  challengeSession = null;
  pendingUsername = null;
}

// Cognito must return a Session alongside an EMAIL_OTP challenge, since that
// session is the only thing a submitted code can be answered against. Entering
// the code step without one strands the user on a form that cannot succeed, so
// the flow stops here instead.
function beginOtpChallenge(session: string | undefined) {
  if (!session) {
    throw new AuthFlowError(
      'EMAIL_OTP challenge arrived with no Session to respond against',
    );
  }
  challengeSession = session;
  useAuthStore.setState({ needsOtpCode: true });
}

// Abandons the challenge in flight and returns the store to the email step.
// Both halves live here because they have to move together: `needsOtpCode` is
// what puts the code form on screen, and the session is what a submitted code
// answers against, so clearing the flag alone would leave a live challenge for
// the abandoned address behind it.
export function cancelOtpChallenge(): void {
  challengeSession = null;
  pendingUsername = null;
  useAuthStore.setState({ needsOtpCode: false, error: null });
}

export async function startSignIn(email: string): Promise<void> {
  useAuthStore.setState({ loading: true, error: null });
  pendingUsername = email.toLowerCase().trim();
  try {
    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_AUTH' as AuthFlowType,
      ClientId: USER_POOL_CLIENT_ID,
      AuthParameters: {
        USERNAME: pendingUsername,
        PREFERRED_CHALLENGE: 'EMAIL_OTP',
      },
    });
    const response = await client.send(command);

    if (response.ChallengeName === 'EMAIL_OTP') {
      beginOtpChallenge(response.Session);
      return;
    }

    if (response.AvailableChallenges) {
      challengeSession = response.Session ?? null;
      await selectEmailOtpChallenge();
      return;
    }

    if (response.AuthenticationResult) {
      await applyAuthResult(response.AuthenticationResult, pendingUsername);
      return;
    }

    throw new AuthFlowError(
      `InitiateAuth returned neither a challenge nor tokens (ChallengeName: ${response.ChallengeName ?? 'none'})`,
    );
  } catch (err) {
    useAuthStore.setState({
      error: friendlyMessage(err, SIGN_IN_ERRORS, 'Sign in failed'),
    });
    throw err;
  } finally {
    useAuthStore.setState({ loading: false });
  }
}

async function selectEmailOtpChallenge() {
  if (!challengeSession || !pendingUsername) {
    throw new AuthFlowError(
      'SELECT_CHALLENGE needs a session and username; the InitiateAuth response carried no Session',
    );
  }
  const command = new RespondToAuthChallengeCommand({
    ClientId: USER_POOL_CLIENT_ID,
    ChallengeName: 'SELECT_CHALLENGE',
    Session: challengeSession,
    ChallengeResponses: {
      USERNAME: pendingUsername,
      ANSWER: 'EMAIL_OTP',
    },
  });
  const response = await client.send(command);
  if (response.ChallengeName === 'EMAIL_OTP') {
    beginOtpChallenge(response.Session);
    return;
  }
  if (response.AuthenticationResult) {
    await applyAuthResult(response.AuthenticationResult, pendingUsername);
    return;
  }
  throw new AuthFlowError(
    `SELECT_CHALLENGE returned neither EMAIL_OTP nor tokens (ChallengeName: ${response.ChallengeName ?? 'none'})`,
  );
}

export async function submitOtpCode(otpCode: string): Promise<void> {
  useAuthStore.setState({ loading: true, error: null });
  try {
    // Inside the try so the store records a message: Login renders only what
    // the store holds, and a throw from out here left the code form silent.
    if (!challengeSession || !pendingUsername) {
      throw new AuthFlowError(
        'A code was submitted with no challenge session in flight',
      );
    }
    const command = new RespondToAuthChallengeCommand({
      ClientId: USER_POOL_CLIENT_ID,
      ChallengeName: 'EMAIL_OTP',
      Session: challengeSession,
      ChallengeResponses: {
        USERNAME: pendingUsername,
        EMAIL_OTP_CODE: otpCode,
      },
    });
    const response = await client.send(command);
    if (!response.AuthenticationResult) {
      throw new AuthFlowError(
        `EMAIL_OTP was accepted but returned no tokens (ChallengeName: ${response.ChallengeName ?? 'none'})`,
      );
    }
    await applyAuthResult(response.AuthenticationResult, pendingUsername);
  } catch (err) {
    useAuthStore.setState({
      error: friendlyMessage(err, OTP_ERRORS, 'OTP verification failed'),
    });
    throw err;
  } finally {
    useAuthStore.setState({ loading: false });
  }
}

// Thrown when a refresh could not be completed rather than refused. The stored
// session survives it: its refresh token was never judged, so it is still the
// right one to try again with.
export class TokenRefreshUnavailableError extends Error {
  override name = 'TokenRefreshUnavailableError';
}

// `refused` is Cognito's verdict on the refresh token; `unavailable` is every
// way the question can go unanswered.
type RefreshOutcome =
  | { status: 'renewed'; session: Session }
  | { status: 'refused' }
  | { status: 'unavailable'; cause: unknown };

// The exceptions that mean this refresh token will not work again: it was
// revoked or has expired, the account behind it is gone or cannot sign in, or
// the request named something that does not exist. Everything else — a network
// that never delivered the call, a 5xx, throttling — leaves the token's
// standing unknown, and the default below treats it that way.
const REFRESH_REFUSED_ERRORS = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'PasswordResetRequiredException',
  'InvalidParameterException',
  'ResourceNotFoundException',
]);

// `viaHostedUi` comes from the session being renewed: Cognito's refresh
// response says nothing about which flow first issued the tokens.
async function refreshTokens(
  refreshToken: string,
  viaHostedUi?: boolean,
): Promise<RefreshOutcome> {
  try {
    const command = new InitiateAuthCommand({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: USER_POOL_CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: refreshToken },
    });
    const response = await client.send(command);
    const result = response.AuthenticationResult;
    if (!result?.AccessToken || !result?.IdToken) {
      // A success carrying no tokens is not a verdict on the refresh token,
      // but asking again cannot produce them either.
      return { status: 'refused' };
    }
    return {
      status: 'renewed',
      session: {
        accessToken: result.AccessToken,
        idToken: result.IdToken,
        refreshToken: result.RefreshToken ?? refreshToken,
        email: extractEmail(result.IdToken),
        viaHostedUi,
      },
    };
  } catch (err) {
    if (err instanceof Error && REFRESH_REFUSED_ERRORS.has(err.name)) {
      return { status: 'refused' };
    }
    // Reached only after the SDK's own retries are spent: it makes three
    // attempts by default and already backs off on network failures, 5xx
    // responses and throttling.
    return { status: 'unavailable', cause: err };
  }
}

// Set for the duration of one REFRESH_TOKEN_AUTH call so that callers arriving
// while it is in flight wait on it instead of starting their own. Both axios
// interceptors refresh-check every request, so a page issuing four queries at
// once on a stale token otherwise sends four refreshes; worse, a single one of
// them failing clears the session that the other three just renewed.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

// Bumped whenever the stored session is replaced or dropped by something other
// than a refresh. A refresh outlives the session it was started for: the user
// can sign out, or sign in as someone else, in the second Cognito takes to
// answer. The awaiting caller compares this against the value it captured and
// throws the answer away if it no longer belongs to the current session.
let sessionEpoch = 0;

// Abandons any refresh in flight. Both halves are needed: the epoch bump makes
// the awaiting caller discard the answer it is about to receive, and clearing
// the slot keeps the next session from being handed a refresh issued against
// the previous account's refresh token.
function invalidateRefresh() {
  sessionEpoch += 1;
  refreshInFlight = null;
}

export async function ensureValidToken(): Promise<string | null> {
  const current = useAuthStore.getState().session ?? loadSession();
  if (!current?.idToken) return null;

  if (!isTokenExpired(current.idToken)) {
    return current.idToken;
  }

  if (current.refreshToken) {
    const epoch = sessionEpoch;
    // Released in `finally` rather than after the await: callers that arrive
    // once it has settled re-read the store above, see the token it produced,
    // and never reach this branch. The identity check leaves a newer attempt
    // alone, since an abandoned one still settles and would clear its slot.
    if (!refreshInFlight) {
      const attempt: Promise<RefreshOutcome> = refreshTokens(
        current.refreshToken,
        current.viaHostedUi,
      ).finally(() => {
        if (refreshInFlight === attempt) refreshInFlight = null;
      });
      refreshInFlight = attempt;
    }
    const outcome = await refreshInFlight;
    // Returning rather than falling through: the session this refresh was for
    // is already gone, and whatever replaced it is not this call's to clear.
    if (sessionEpoch !== epoch) return null;

    if (outcome.status === 'renewed') {
      saveSession(outcome.session);
      useAuthStore.setState({ session: outcome.session });
      return outcome.session.idToken;
    }
    if (outcome.status === 'unavailable') {
      // Left in place for the next attempt. Callers distinguish this from a
      // null return, which says the session is over and the user has to sign
      // in again.
      throw new TokenRefreshUnavailableError(
        'Could not reach Cognito to refresh the session',
        { cause: outcome.cause },
      );
    }
  }

  clearStoredSession();
  useAuthStore.setState({ session: null });
  return null;
}

export async function handleOAuthCallback(
  code: string,
  state: string | null,
): Promise<void> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_KEY);
  // Cleared before either check, so a rejected attempt leaves nothing behind
  // for a second code to be redeemed against.
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(PKCE_KEY);

  // A code arriving without a matching state was not requested from this
  // browser. An attacker who completes their own Google sign-in and then sends
  // the victim their callback URL would otherwise sign the victim into the
  // attacker's account, so the exchange must not happen at all.
  if (!expectedState || state !== expectedState) {
    throw new AuthFlowError(
      `OAuth callback state does not match the value stored at redirect (received: ${state ?? 'none'})`,
    );
  }
  if (!verifier) {
    throw new AuthFlowError(
      'OAuth callback arrived with no stored PKCE verifier',
    );
  }

  const tokenEndpoint = hostedUiUrl('/oauth2/token');
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: USER_POOL_CLIENT_ID,
    code,
    code_verifier: verifier,
    redirect_uri: getRedirectUri(),
  });
  const res = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }
  const data = (await res.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
  };
  await applyAuthResult(
    {
      AccessToken: data.access_token,
      IdToken: data.id_token,
      RefreshToken: data.refresh_token,
    } as AuthenticationResultType,
    extractEmail(data.id_token),
    true,
  );
}

// Long enough for a round trip to Cognito, short enough that an unreachable
// network cannot strand the user on a page that already looks signed out.
const REVOKE_TIMEOUT_MS = 3000;

// Ends the refresh token's remaining 30 days, and with it every access token
// minted from it. Failures are reported and swallowed: a sign-out Cognito never
// hears about is worse than one the user cannot complete, and there is nothing
// the caller can do about it either way.
async function revokeRefreshToken(refreshToken: string): Promise<void> {
  try {
    const res = await fetch(hostedUiUrl('/oauth2/revoke'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token: refreshToken,
        client_id: USER_POOL_CLIENT_ID,
      }).toString(),
      // Callers reload or navigate away the moment this settles, which would
      // otherwise cancel the request in flight.
      keepalive: true,
      signal: AbortSignal.timeout(REVOKE_TIMEOUT_MS),
    });
    if (!res.ok) {
      console.error(
        `Cognito refused to revoke the refresh token: ${res.status}`,
      );
    }
  } catch (err) {
    console.error('Could not reach Cognito to revoke the refresh token:', err);
  }
}

// Drops the Cognito session cookie the hosted UI set. Without it, a later call
// to /oauth2/authorize answers the next Google sign-in with a code for the user
// who just left, no prompt shown.
function hostedUiLogoutUrl(): string {
  const params = new URLSearchParams({
    client_id: USER_POOL_CLIENT_ID,
    // Matched against the app client's logout_urls exactly; Cognito shows an
    // error page rather than redirecting if it is not among them.
    logout_uri: `${window.location.origin}/login`,
  });
  return hostedUiUrl(`/logout?${params.toString()}`);
}

// Returns whether it navigated away, which tells the caller not to route
// anywhere itself. Pass `redirect` for a sign-out the user asked for; a session
// that came through the hosted UI then continues to Cognito's /logout. The
// axios interceptors leave it unset, since they sign out mid-request and
// recover in place.
export async function signOut(options?: {
  redirect?: boolean;
}): Promise<boolean> {
  // Layout empties localStorage before calling this, so the stored copy can
  // already be gone; the store still holds the token the revoke needs.
  const current = useAuthStore.getState().session ?? loadSession();

  invalidateRefresh();
  clearStoredSession();
  useAuthStore.setState({
    session: null,
    needsOtpCode: false,
    error: null,
  });
  challengeSession = null;
  pendingUsername = null;

  if (current?.refreshToken) {
    await revokeRefreshToken(current.refreshToken);
  }

  if (options?.redirect && current?.viaHostedUi) {
    window.location.assign(hostedUiLogoutUrl());
    return true;
  }
  return false;
}
